import { useState, useEffect } from 'react';
import { Button, Form } from 'react-bootstrap';
import { RefundPolicyText } from '../components/RefundPolicyText';
import { checkout } from '../services/DonationStoreService';
import { validateCreatorCode } from '../services/CreatorService';
import { getPackages } from '../services/PackageService';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { EmptyState } from '../components/EmptyState';
import { AccountPageHeader } from '../components/AccountPageHeader';
import CoinImage from "../images/pcoin.webp";
import ScrollIcon from "../images/scroll_icon.png";

function formatCps(n) {
  return Math.round(n).toLocaleString();
}

function PackageCard({ pkg, cpPerCoin, selected, onSelect, showBonus }) {
  const bonusCps = showBonus ? Math.round(pkg.coinCount * cpPerCoin * pkg.bonusPct / 100) : 0;
  const hasBonus = showBonus && bonusCps > 0;

  const cardStyle = pkg.outlineColor
    ? { '--pkg-outline': pkg.outlineColor }
    : undefined;

  return (
    <button
      type="button"
      className={`pkg-card${selected ? ' pkg-card--selected' : ''}${pkg.outlineColor ? ' pkg-card--highlighted' : ''}`}
      style={cardStyle}
      onClick={() => onSelect(pkg.id)}
      aria-pressed={selected}
    >
      {pkg.tagline && <span className="pkg-tagline">{pkg.tagline}</span>}
      <div className="pkg-coin-row">
        <img src={CoinImage} alt="" className="pkg-coin-icon" />
        <span className="pkg-coin-count">{pkg.coinCount}</span>
        <span className="pkg-coin-label">Coins</span>
      </div>
      <p className="pkg-price">${pkg.coinCount}.00 <span className="pkg-price-currency">USD</span></p>
      {hasBonus && (
        <p className="pkg-bonus">+{formatCps(bonusCps)} bonus CPs</p>
      )}
    </button>
  );
}

function CreatorCodeSection({ onApplied, appliedCode, onRemove }) {
  const [input, setInput] = useState('');
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);

  async function handleApply() {
    const code = input.trim();
    if (!code) return;
    setApplying(true);
    setError(null);
    try {
      const result = await validateCreatorCode(code);
      onApplied({
        code: result?.code ?? code,
        creatorName: result?.creatorName ?? '',
      });
      setInput('');
    } catch (err) {
      if (err.message === 'own-code') {
        setError("You can't apply your own creator code.");
      } else {
        setError("That creator code isn't valid.");
      }
    } finally {
      setApplying(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApply();
    }
  }

  if (appliedCode) {
    return (
      <div className="bc-creator-section">
        <p className="bc-bonus-notice">
          A creator code grants you bonus CPs with your purchase.
        </p>
        <div className="bc-code-applied">
          <div className="bc-code-applied-main">
            <span className="bc-code-applied-code">{appliedCode.code.toUpperCase()}</span>
            <span className="bc-code-applied-name">Supporting {appliedCode.creatorName}</span>
          </div>
          <button
            type="button"
            className="bc-code-remove"
            onClick={onRemove}
            aria-label="Remove creator code"
          >✕</button>
        </div>
      </div>
    );
  }

  return (
    <div className="bc-creator-section">
      <label className="bc-amount-label bc-code-label">
        Creator Code <span className="bc-code-optional">(optional)</span>
      </label>
      <p className="bc-bonus-notice">
        Applying a creator code grants you bonus CPs with your purchase.
      </p>
      <div className="bc-code-row">
        <Form.Control
          className={`bc-code-input${error ? ' bc-code-input--invalid' : ''}`}
          type="text"
          placeholder="Enter a creator code"
          value={input}
          onChange={e => { setInput(e.target.value); setError(null); }}
          onKeyDown={handleKeyDown}
          maxLength={32}
          disabled={applying}
        />
        <Button
          className="bc-apply-btn"
          type="button"
          onClick={handleApply}
          disabled={applying || !input.trim()}
        >
          {applying ? 'Checking\u2026' : 'Apply'}
        </Button>
      </div>
      {error && <p className="bc-code-feedback bc-code-feedback--invalid">{error}</p>}
    </div>
  );
}

function BuyCoinsForm({ packages, cpPerCoin }) {
  const [selectedId, setSelectedId] = useState(null);
  const [appliedCode, setAppliedCode] = useState(null); // null | { code, creatorName }
  const [purchasing, setPurchasing] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);

  async function purchaseCoins(e) {
    e.preventDefault();
    if (!selectedId) return;
    setPurchasing(true);
    setCheckoutError(null);
    try {
      const checkoutLink = await checkout(selectedId, appliedCode?.code);
      window.location.replace(checkoutLink);
    } catch (err) {
      if (err.message === 'invalid-creator-code') {
        setAppliedCode(null);
        setCheckoutError('The creator code is no longer valid.');
      } else {
        setCheckoutError('Something went wrong. Please try again.');
      }
      setPurchasing(false);
    }
  }

  return (
    <div className="register-page">
      <AccountPageHeader
        icon={<img src={CoinImage} alt="" className="bc-header-icon" />}
        title="Paragon Coins"
        description="$1.00 USD per coin — select a package below"
      />
      <div className="register-card bc-card">
        <p className="bc-description">
          Paragon Coins can be spent in-game at the PrizeNPC in the Market.
          Purchasing helps support server upkeep and development.
        </p>

        <div className="pkg-grid">
          {packages.map(pkg => (
            <PackageCard
              key={pkg.id}
              pkg={pkg}
              cpPerCoin={cpPerCoin}
              selected={selectedId === pkg.id}
              onSelect={setSelectedId}
              showBonus={!!appliedCode}
            />
          ))}
        </div>

        <Form onSubmit={purchaseCoins}>
          <CreatorCodeSection
            appliedCode={appliedCode}
            onApplied={setAppliedCode}
            onRemove={() => setAppliedCode(null)}
          />

          {checkoutError && (
            <p className="bc-code-feedback bc-code-feedback--invalid">{checkoutError}</p>
          )}

          <Button
            className="btn-register"
            type="submit"
            disabled={purchasing || !selectedId}
          >
            {purchasing ? "Redirecting to checkout\u2026" : "Purchase Now"}
          </Button>
        </Form>
      </div>
    </div>
  );
}

function BuyCoins() {
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [packages, setPackages] = useState([]);
  const [cpPerCoin, setCpPerCoin] = useState(150_000_000);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await getPackages();
        if (!cancelled) {
          setPackages(data.packages);
          setCpPerCoin(data.cpPerCoin);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  if (!policyAccepted) {
    return (
      <div className="register-page">
        <AccountPageHeader
          icon={<img src={ScrollIcon} alt="" className="bc-header-icon" />}
          title="Paragon Coins"
          description="Please read the refund policy before purchasing"
        />
        <div className="register-card bc-card">
          <div className="bc-policy-body">
            <RefundPolicyText />
          </div>
          <Button className="btn-register" onClick={() => setPolicyAccepted(true)}>
            Accept &amp; Continue
          </Button>
        </div>
      </div>
    );
  }

  if (loading) return <LoadingSpinner />;
  if (error) return <EmptyState>Failed to load packages. Please try again.</EmptyState>;
  if (packages.length === 0) return <EmptyState>No packages are currently available.</EmptyState>;

  return <BuyCoinsForm packages={packages} cpPerCoin={cpPerCoin} />;
}

export { BuyCoins };
